import React, { useState, useEffect } from 'react';
import { PlayCircle, BookOpen } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { apiService } from '../services/apiService';

interface Contest {
  id: string;
  name: string;
  year: number;
  contestType: string;
  version: string;
  totalProblems: number;
  userProgress: {
    attempted: number;
    isCompleted: boolean;
    hasStarted: boolean;
    isTimedOut?: boolean;
    canViewResults?: boolean;
  };
}

interface AMCTestSelectionProps {
  onTestSelect: (contestId: string, isCompleted?: boolean) => void;
}

type ContestTypeFilter = 'All' | 'AMC 10' | 'AMC 12';

const AMCTestSelection: React.FC<AMCTestSelectionProps> = ({ onTestSelect }) => {
  const { token } = useUser();
  const [contests, setContests] = useState<Contest[]>([]);
  const [typeFilter, setTypeFilter] = useState<ContestTypeFilter>('All');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (token) {
      fetchContests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchContests = async () => {
    console.log('AMC fetchContests called, token available:', !!token);
    
    if (!token) {
      console.error('No authentication token available for AMC API');
      setError('Authentication required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('Making AMC contests API call to:', `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/amc/contests`);
      const data = await apiService.get('/amc/contests');
      
      console.log('AMC contests API response:', data);
      
      if (data.success) {
        setContests(data.contests);
      } else {
        throw new Error(data.message || 'Failed to fetch contests');
      }
    } catch (err) {
      console.error('AMC fetchContests error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load contests');
    } finally {
      setLoading(false);
    }
  };

  const getButtonText = (contest: Contest) => {
    // Check if the contest has timeout information
    if (contest.userProgress.isTimedOut) return 'Review Results';
    if (contest.userProgress.isCompleted) return 'Review';
    if (contest.userProgress.hasStarted) return 'Continue';
    return 'Start Test';
  };

  const getButtonIcon = (contest: Contest) => {
    if (contest.userProgress.isCompleted) return <BookOpen size={16} />;
    return <PlayCircle size={16} />;
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        paddingTop: '24px',
        background: 'linear-gradient(135deg, #3F8EF6 0%, #A0F0D0 100%)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '3px solid rgba(255, 255, 255, 0.3)',
            borderTopColor: 'white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <p style={{ color: 'white', fontSize: '18px' }}>Loading AMC tests...</p>
        </div>
        <style>
          {`@keyframes spin { to { transform: rotate(360deg); } }`}
        </style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        paddingTop: '24px',
        background: 'linear-gradient(135deg, #3F8EF6 0%, #A0F0D0 100%)'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          padding: '40px',
          borderRadius: '16px',
          textAlign: 'center',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          <p style={{ color: '#ef4444', marginBottom: '20px', fontSize: '18px' }}>{error}</p>
          <button 
            onClick={fetchContests}
            style={{
              background: 'linear-gradient(135deg, #3F8EF6, #A0F0D0)',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const visibleContests = typeFilter === 'All'
    ? contests
    : contests.filter((contest) => contest.contestType === typeFilter);

  const groupedContests = visibleContests.reduce((acc, contest) => {
    const key = `${contest.year}_${contest.contestType}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(contest);
    return acc;
  }, {} as Record<string, Contest[]>);

  return (
    <div style={{
      background: 'linear-gradient(135deg, #3F8EF6 0%, #A0F0D0 100%)',
      minHeight: '100vh',
      paddingTop: '24px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '24px',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '24px'
      }}>
        {/* Header */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '32px',
          marginBottom: '0',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          gridColumn: '1 / -1'
        }}>
          <h1 style={{
            fontSize: '32px',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #3F8EF6, #A0F0D0)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '12px'
          }}>AMC Practice Tests</h1>
          <p style={{
            color: '#64748b',
            fontSize: '16px',
            lineHeight: '1.5',
            marginBottom: '16px'
          }}>
            Practice with official AMC 10 and AMC 12 problems. Track your progress and review detailed solutions.
          </p>
          <div style={{ display: 'flex', gap: '8px' }}>
            {(['All', 'AMC 10', 'AMC 12'] as ContestTypeFilter[]).map((filter) => (
              <button
                key={filter}
                onClick={() => setTypeFilter(filter)}
                style={{
                  padding: '8px 20px',
                  borderRadius: '9999px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  ...(typeFilter === filter ? {
                    background: 'linear-gradient(135deg, #3F8EF6, #A0F0D0)',
                    color: 'white',
                    border: 'none'
                  } : {
                    background: '#ffffff',
                    color: '#64748b',
                    border: '1px solid #e2e8f0'
                  })
                }}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>

        {/* Year Sections */}
        {Object.entries(groupedContests)
          .sort(([a], [b]) => b.localeCompare(a))
          .map(([yearType, yearContests]) => {
            const [year, contestType] = yearType.split('_');
            return (
              <div key={yearType} style={{ marginBottom: '0' }}>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '12px 12px 0 0',
                  padding: '20px 24px',
                  marginBottom: '0',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)'
                }}>
                  <h2 style={{
                    fontSize: '24px',
                    fontWeight: '600',
                    color: '#1e293b'
                  }}>
                    {year} {contestType}
                  </h2>
                </div>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '0 0 12px 12px',
                  padding: '16px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  {yearContests
                    .sort((a, b) => a.version.localeCompare(b.version))
                    .map((contest) => (
                      <div
                        key={contest.id}
                        style={{
                          padding: '12px',
                          border: '1px solid #f1f5f9',
                          borderRadius: '8px',
                          transition: 'all 0.3s ease',
                          position: 'relative',
                          background: '#ffffff',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(63, 142, 246, 0.03)';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 16px rgba(63, 142, 246, 0.15)';
                          e.currentTarget.style.borderColor = '#e2e8f0';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#ffffff';
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                          e.currentTarget.style.borderColor = '#f1f5f9';
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <div style={{ flex: '1' }}>
                            <div style={{
                              fontSize: '14px',
                              fontWeight: '600',
                              color: '#1e293b',
                              marginBottom: '4px'
                            }}>
                              {contest.contestType} {contest.version}
                            </div>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '10px',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                              letterSpacing: '0.3px',
                              alignSelf: 'flex-start',
                              ...(contest.userProgress.isCompleted ? {
                                background: 'linear-gradient(135deg, #10b981, #A0F0D0)',
                                color: 'white'
                              } : {
                                background: '#f1f5f9',
                                color: '#64748b',
                                border: '1px solid #e2e8f0'
                              })
                            }}>
                              {contest.userProgress.isCompleted ? 'Completed' : 'Not Started'}
                            </span>
                          </div>
                          <div style={{
                            display: 'flex',
                            gap: '6px',
                            alignItems: 'center',
                            marginLeft: '12px'
                          }}>
                            <button
                              onClick={() => onTestSelect(contest.id, contest.userProgress.isCompleted)}
                              style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                fontWeight: '500',
                                fontSize: '12px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                textDecoration: 'none',
                                whiteSpace: 'nowrap',
                                ...(contest.userProgress.isCompleted ? {
                                  background: '#ffffff',
                                  color: '#64748b',
                                  border: '2px solid #e2e8f0'
                                } : {
                                  background: 'linear-gradient(135deg, #3F8EF6, #A0F0D0)',
                                  color: 'white',
                                  border: 'none'
                                })
                              }}
                              onMouseEnter={(e) => {
                                if (contest.userProgress.isCompleted) {
                                  e.currentTarget.style.borderColor = '#3F8EF6';
                                  e.currentTarget.style.color = '#3F8EF6';
                                } else {
                                  e.currentTarget.style.background = 'linear-gradient(135deg, #2E7DE5, #8FE9BF)';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(63, 142, 246, 0.3)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (contest.userProgress.isCompleted) {
                                  e.currentTarget.style.borderColor = '#e2e8f0';
                                  e.currentTarget.style.color = '#64748b';
                                } else {
                                  e.currentTarget.style.background = 'linear-gradient(135deg, #3F8EF6, #A0F0D0)';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = 'none';
                                }
                              }}
                            >
                              {getButtonIcon(contest)}
                              {getButtonText(contest)}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            );
          })}

        {contests.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '48px',
            gridColumn: '1 / -1',
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }}>
            <BookOpen size={48} style={{ margin: '0 auto 16px', color: '#9ca3af' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '500', color: '#1f2937', marginBottom: '8px' }}>No Tests Available</h3>
            <p style={{ color: '#6b7280' }}>
              AMC test data is being imported. Please check back later.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AMCTestSelection;